import React, { useState, useEffect } from 'react';
import { getFileUrlRelative } from '../../../utils/fileUrl';
import Loading from '../../loading-animation';
import './SecureImage.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://server.ciertacorp.com';

/**
 * Secure Image Component
 * Loads images with authentication token using fetch API
 * 
 * @param {String} src - S3 key (e.g., "loads/507f1f77bcf86cd799439011/images/file.jpg") or URL
 * @param {String} alt - Alt text
 * @param {Object} props - Other img props
 */
const SecureImage = ({ src, alt = '', className = '', ...props }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setImageUrl(null);
      setLoading(false);
      return;
    }

    // If it's already a blob URL or full URL, use it directly
    if (src.startsWith('blob:') || src.startsWith('http://') || src.startsWith('https://')) {
      setImageUrl(src);
      setLoading(false);
      return;
    }

    // Load image with authentication token
    const loadImage = async () => {
      setLoading(true);
      setError(false);

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError(true);
          setLoading(false);
          return;
        }

        // Build full URL
        const relativeUrl = getFileUrlRelative(src);
        const fullUrl = relativeUrl.startsWith('/') 
          ? `${API_BASE_URL}${relativeUrl}`
          : relativeUrl;

        // Fetch image with token
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }

        // Create blob URL from response
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setImageUrl(blobUrl);
      } catch (err) {
        console.error('Error loading secure image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();

    // Cleanup blob URL on unmount
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={`secure-image-loading ${className}`} {...props}>
        <Loading size="small" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={`secure-image-error ${className}`} {...props}>
        <span>No image</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={`secure-image ${className}`}
      onError={(e) => {
        console.error('Image load error:', e);
        setError(true);
        e.target.style.display = 'none';
      }}
      {...props}
    />
  );
};

export default SecureImage;

